import React, { useState } from 'react';

import {
  InteractiveElement,
  isSameInteractiveElement,
  SankeyInteractionContext,
} from '../../contexts/sankeyInteractionContext';
import { useSankeyRenderContext } from '../../contexts/sankeyRenderContext';
import { Position, SankeyNode } from '../../diagram/types';

type Props = {
  children: React.ReactNode;
};

const SankeyInteractionProvider: React.FC<Props> = ({ children }) => {
  const { graph, nodes, flows, moveNodeTo, saveNodeMove } =
    useSankeyRenderContext();

  const [hintText, setHintText] = useState('');
  const [hoveredElement, setHoveredElement] = useState<InteractiveElement>();
  const [focusedElement, setFocusedElement] = useState<InteractiveElement>();
  const [blockedElement, setBlockedElement] = useState<InteractiveElement>();

  const [hasMovedNode, setHasMovedNode] = useState(false);
  const [draggedNode, setDraggedNode] = useState<SankeyNode>();
  const [dragStartPosition, setDragStartPosition] = useState<Position>();

  const isElementFocused = (element: InteractiveElement) =>
    isSameInteractiveElement(element, focusedElement);
  const focusElement = (element: InteractiveElement) => {
    if (
      !isSameInteractiveElement(element, blockedElement) &&
      !(
        hasMovedNode &&
        element.type === 'node' &&
        element.id === draggedNode?.id
      )
    ) {
      setFocusedElement(element);
    }
  };
  const blurFocusedElement = () => {
    setBlockedElement(focusedElement);
    setTimeout(() => setBlockedElement(undefined), 200);
    setFocusedElement(undefined);
  };

  const isElementHovered = (element: InteractiveElement) =>
    isSameInteractiveElement(element, hoveredElement);
  const handleMouseOver = (element: InteractiveElement) =>
    setHoveredElement(element);
  const handleMouseOut = () => setHoveredElement(undefined);

  const isElementNodeOfFlow = (
    nodeElement: InteractiveElement,
    flowElement: InteractiveElement | undefined
  ) => {
    if (flowElement?.type === 'flow' && nodeElement.type === 'node') {
      const flow = flows.find((f) => f.id === flowElement.id);
      return (
        flow?.source.id === nodeElement.id || flow?.target.id === nodeElement.id
      );
    }
    return false;
  };

  const isElementHighlighted = (element: InteractiveElement) =>
    isElementHovered(element) ||
    isElementFocused(element) ||
    isElementNodeOfFlow(element, hoveredElement) ||
    isElementNodeOfFlow(element, focusedElement);

  const isElementFaded = (element: InteractiveElement) => {
    if (!focusedElement && !hoveredElement) {
      return false;
    }
    if (isElementHighlighted(element)) {
      return false;
    }
    return true;
  };

  // Show a hint if the user is dragging a node and not holding shift and record the dragged node
  const handleNodeDragStart = (
    ev: React.MouseEvent<SVGElement, MouseEvent>,
    node: SankeyNode
  ) => {
    setDraggedNode(nodes.find((n) => n.id === node.id));
    setDragStartPosition({ x: ev.screenX, y: ev.screenY });
    setHintText('Hold down Shift to move the node in only one direction');
  };

  const handleNodeDragMove = (ev: React.MouseEvent<SVGElement, MouseEvent>) => {
    // Do nothing if no node is being dragged
    if (!draggedNode || !dragStartPosition) {
      return;
    }

    const lastX = draggedNode.lastPos?.x ?? NaN;
    const lastY = draggedNode.lastPos?.y ?? NaN;
    const origX = draggedNode.origPos?.x ?? NaN;
    const origY = draggedNode.origPos?.y ?? NaN;
    const nodeDx = draggedNode.dx ?? NaN;
    const nodeDy = draggedNode.dy ?? NaN;
    let movedX = ev.screenX - dragStartPosition.x;
    let movedY = ev.screenY - dragStartPosition.y;

    if (ev.shiftKey) {
      // Shift is pressed, so this is a constrained drag - figure out which direction the user has dragged further in:
      if (Math.abs(movedX) > Math.abs(movedY)) {
        movedY = 0; // Use X move; keep Y constant
      } else {
        movedX = 0; // Use Y move; keep X constant
      }
    }

    // NOTE: We don't saveNodeMove after every pixel-move of a drag; just when a gesture is finished
    moveNodeTo(
      draggedNode,
      // Calculate the percentages we want to save (which will stay
      // independent of the graph's edge constraints, even if the spacing,
      // etc. changes to distort them):
      [
        (movedX + lastX - origX) / (graph.w - nodeDx),
        graph.h === draggedNode.dy
          ? 0
          : (movedY + lastY - origY) / (graph.h - nodeDy),
      ]
    );

    // TODO: Replace this with a better way to force node component rerender
    setDragStartPosition({ ...dragStartPosition });
    setHasMovedNode(true);
  };

  // Remove the hint and dragged node and record the node's new position after the drag
  // TODO: Investigate as this is called on every ordinary *click* as well;
  // look into skipping this work if no actual move has happened.
  const handleNodeDragEnd = () => {
    if (!draggedNode) {
      return;
    }
    setTimeout(() => {
      saveNodeMove(draggedNode);
      setDragStartPosition(undefined);
      setDraggedNode(undefined);
      setHasMovedNode(false);
      setHintText('');
    }, 0);
  };

  return (
    <SankeyInteractionContext.Provider
      value={{
        focusElement,
        blurFocusedElement,
        handleMouseOver,
        handleMouseOut,
        isDiagramInteractive: true,
        isElementFocused,
        isElementFaded,
        isElementHighlighted,
        hintText,
        setHintText,
        draggedNode,
        handleNodeDragStart,
        handleNodeDragMove,
        handleNodeDragEnd,
      }}
    >
      {children}
    </SankeyInteractionContext.Provider>
  );
};

export default SankeyInteractionProvider;
